import React, { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import { createAdminVendorsStore } from './createAdminVendorsStore';
import type { AdminVendorsStore, VendorsFetcher } from './types';

type AdminVendorsStoreApi = StoreApi<AdminVendorsStore>;

const AdminVendorsStoreContext = createContext<AdminVendorsStoreApi | null>(null);

interface AdminVendorsStoreProviderProps {
  /** Injectable loader (tests); defaults to DataService.getAllVendors. */
  fetchVendors?: VendorsFetcher;
  children: React.ReactNode;
}

export function AdminVendorsStoreProvider({
  fetchVendors,
  children,
}: AdminVendorsStoreProviderProps): React.JSX.Element {
  const storeRef = useRef<AdminVendorsStoreApi | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createAdminVendorsStore(fetchVendors);
  }
  return (
    <AdminVendorsStoreContext.Provider value={storeRef.current}>
      {children}
    </AdminVendorsStoreContext.Provider>
  );
}

export function useAdminVendorsStore<T>(selector: (s: AdminVendorsStore) => T): T {
  const store = useContext(AdminVendorsStoreContext);
  if (!store) {
    throw new Error('useAdminVendorsStore must be used within AdminVendorsStoreProvider');
  }
  return useStore(store, selector);
}
